import { Button, Modal } from '@insurancemenu/imenu-components-lib';
import { Add } from '@insurancemenu/imenu-components-lib/dist/icons';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useRootStore } from 'store/rootStore';
import { createStyles as createModalStyles } from 'components/common/modals/ActionModal/styles';
import clsx from 'clsx';
import EmployerBlock from './EmployerBlock';
import EmployerForm from './EmployerForm';
import { createStyles } from './styles';

interface EditEmployersModalProps {
  groupId: string;
}

/**
 * @deprecated Use InviteEmployersModal where possible.
 *
 * This modal is still in use due to legacy group profile architecture dependencies:
 * - Integrates with the domain group store and deprecated group controller
 * - Used in the group sidebar component
 *
 * Once the group profile architecture is refactored and new group profile page is created, this component should be
 * replaced with InviteEmployersModal and can be removed.
 */
const EditEmployersModal = observer(({ groupId }: EditEmployersModalProps): JSX.Element => {
  const {
    additionalAccessStore: {
      isEditEmployersModalOpen,
      groupEmployers,
      loading,
      addEmployerForm,
      addEmployer,
      closeEditEmployersModal,
    },
    groupSidebarStore: {
      group: { contactInfo },
    },
  } = useRootStore();

  const convertedGroupEmployers = groupEmployers?.map((employer) => toJS(employer)).slice();
  const groupEmployersWithoutAdmin = convertedGroupEmployers.filter(({ email }) => email !== contactInfo.primaryEmail);
  const groupAdmin = convertedGroupEmployers.find(({ email }) => email === contactInfo.primaryEmail);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (addEmployerForm.isFormValid()) {
      await addEmployer(groupId);
    }
  };

  const classes = createStyles();
  const modalStyles = createModalStyles(true); // common modal styles

  return (
    <Modal
      show={isEditEmployersModalOpen}
      onClose={closeEditEmployersModal}
      closable
      className={clsx(modalStyles.modal, classes.employersModal)}
      id="employers-info"
    >
      {loading ? (
        <FormsSpinner display={loading} />
      ) : (
        <>
          <h3 className={modalStyles.modalTitle}>Group Access</h3>
          <p className={modalStyles.contentText}>
            Invite users from the group to create an account and access the checklist.
          </p>
          {convertedGroupEmployers.length > 0 ? (
            <div className={classes.employersWrapper}>
              {groupAdmin && (
                <div className={classes.groupAdminSection}>
                  <p className={classes.groupAdminSectionLabel}>Group Admin</p>
                  <EmployerBlock key={groupAdmin.id} groupId={groupId} employer={groupAdmin} isGroupAdmin />
                </div>
              )}
              {groupEmployersWithoutAdmin.length > 0 && (
                <div>
                  <p className={classes.employerSectionLabel}>Employers</p>
                  {groupEmployersWithoutAdmin.map((employer) => {
                    return (
                      <EmployerBlock key={employer.id} groupId={groupId} employer={employer} isGroupAdmin={false} />
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <p id="employers-error-message" className={clsx(modalStyles.contentText, classes.noDataMessage)}>
              No added group users. Add the first one in the form below.
            </p>
          )}
          <EmployerForm
            formButtonIcon={<Add />}
            form={addEmployerForm}
            actionBtnText="Invite Employer"
            onAction={onSubmit}
            formClassName={classes.employerForm}
          />
          <Button
            type="outlined"
            className={classes.cancelButton}
            color="primary"
            onClick={closeEditEmployersModal}
            size="large"
          >
            Close
          </Button>
        </>
      )}
    </Modal>
  );
});

export default EditEmployersModal;
